import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet } from '../utils/apiClient';
import type {
  WeeklyClassSheetDay,
  WeeklyClassSheetMetadata,
  WeeklyClassSheetOptions,
  WeeklyClassSheetResponse,
} from '../types/WeeklyClassSheet.type';
import { buildClassCalendarParams } from '../utils/classCalendarQuery';

interface UseWeeklyClassSheetResult {
  days: WeeklyClassSheetDay[];
  metadata: WeeklyClassSheetMetadata | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useWeeklyClassSheet = ({
  role,
  enabled,
  range,
  filters,
}: WeeklyClassSheetOptions): UseWeeklyClassSheetResult => {
  const [days, setDays] = useState<WeeklyClassSheetDay[]>([]);
  const [metadata, setMetadata] = useState<WeeklyClassSheetMetadata | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const requestSequence = useRef(0);
  const filterSignature = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();
    const sequence = ++requestSequence.current;
    const loadSheet = async () => {
      setLoading(true);
      setError(null);

      const params = buildClassCalendarParams(range, filters);
      const endpoint =
        role === 'coach' ? '/class/coach/weekly-sheet' : '/class/weekly-sheet';

      try {
        const response = await apiGet<WeeklyClassSheetResponse>(
          `${endpoint}?${params.toString()}`,
          { signal: controller.signal },
        );

        if (controller.signal.aborted || sequence !== requestSequence.current) {
          return;
        }

        setDays(response.data || []);
        setMetadata(response.metadata || null);
      } catch (requestError) {
        if (controller.signal.aborted || sequence !== requestSequence.current) {
          return;
        }

        setError(
          requestError instanceof Error
            ? requestError.message
            : 'Unable to load weekly class sheet.',
        );
        setDays([]);
        setMetadata(null);
      } finally {
        if (
          !controller.signal.aborted &&
          sequence === requestSequence.current
        ) {
          setLoading(false);
        }
      }
    };

    void loadSheet();
    return () => controller.abort();
  }, [enabled, filterSignature, filters, range, refreshToken, role]);

  const refresh = useCallback(() => {
    setRefreshToken((current) => current + 1);
  }, []);

  return { days, metadata, loading, error, refresh };
};
